import {cloneDeep, isArray, isBoolean, isEmpty, isNil, isNumber, isPlainObject, isString} from 'lodash';
import {SpecContext} from '../spec/SpecModel';
import {applyEach, check, checkField, mergeFormat, OpError, validateFormat} from '../spec/SpecOps';
import {ApplySpecOpsResult, RejectedSpecOp} from '../spec/SpecTypes';
import {
    AXIS_IDS,
    AxisId,
    CANDLE_STYLES,
    CHART_SPEC_VERSION,
    CHART_TYPES,
    ChartSpec,
    ChartSpecAxis,
    ChartSpecCapabilities,
    ChartSpecOhlc,
    ChartSpecSeries,
    DATA_LABELS,
    DEFAULT_CHART_SPEC,
    LEGEND_POSITIONS,
    MAX_INNER_SIZE,
    OHLC_KEYS,
    SERIES_COLORS,
    SERIES_TYPES,
    STACKINGS
} from './ChartSpecTypes';

/**
 * ChartSpecOp - the one vocabulary for changing a ChartSpec, applied by `applyOps()`.
 *
 * As with GridSpecOps, ops are intent-level and name fields rather than positions, so AI ops
 * computed against a spec the user has since edited still apply cleanly on top of those edits.
 */
export type ChartSpecOp =
    | {op: 'setOptions'; options: ChartSpecOptions}
    | {op: 'addSeries'; series: ChartSpecSeries; index?: number}
    | {op: 'updateSeries'; field: string; changes: ChartSpecSeriesChanges}
    | {op: 'removeSeries'; field: string}
    | {op: 'moveSeries'; field: string; toIndex: number}
    | {op: 'updateAxis'; axis: AxisId; changes: ChartSpecAxisChanges}
    | {op: 'replaceSpec'; spec: ChartSpec};

export type ChartSpecOptions = Partial<
    Pick<ChartSpec, 'type' | 'layout' | 'xField' | 'ohlc' | 'candleStyle' | 'legend' | 'stacking' | 'dataLabels' | 'sharedTooltip' | 'innerSize' | 'markers'>
>;

/** Series changes - a `null` value removes the setting. */
export type ChartSpecSeriesChanges = {[K in keyof Omit<ChartSpecSeries, 'field'>]?: ChartSpecSeries[K] | null};

/** Axis changes - a `null` value removes the setting. `format` is merged into the existing format. */
export type ChartSpecAxisChanges = {[K in keyof ChartSpecAxis]?: ChartSpecAxis[K] | null};

export type ChartSpecContext = SpecContext<ChartSpecCapabilities>;
export type RejectedChartOp = RejectedSpecOp<ChartSpecOp>;
export type ApplyChartOpsResult = ApplySpecOpsResult<ChartSpec, ChartSpecOp>;

/** Settings that only apply in the 'series' layout. */
const SERIES_LAYOUT_ONLY: (keyof ChartSpecSeries)[] = ['type', 'axis', 'dashed'];

/**
 * Apply ops to a spec, returning a new spec. Each op is validated against the catalog and host
 * capabilities; invalid ops are skipped and reported, the rest still apply.
 */
export function applyOps(spec: ChartSpec, ops: ChartSpecOp[], ctx: ChartSpecContext): ApplyChartOpsResult {
    return applyEach(spec, ops, (draft, op) => applyOp(draft, op, ctx));
}

/** Fill in missing properties (e.g. from older persisted state) and upgrade older versions. */
export function normalizeSpec(spec: Partial<ChartSpec>, defaultSpec: ChartSpec): ChartSpec {
    if (!spec) return cloneDeep(defaultSpec);
    const axes = spec.axes ?? defaultSpec.axes,
        ret: ChartSpec = cloneDeep({
            ...DEFAULT_CHART_SPEC,
            ...defaultSpec,
            ...spec,
            series: spec.series ?? defaultSpec.series,
            axes: {x: {...axes?.x}, primary: {...axes?.primary}, secondary: {...axes?.secondary}},
            version: CHART_SPEC_VERSION
        });

    // Early saved specs had on/off data labels, and a `donut` flag instead of innerSize
    const legacy = spec as any;
    if (isBoolean(legacy.dataLabels)) {
        const named = ret.type === 'pie' || ret.layout === 'categories';
        ret.dataLabels = legacy.dataLabels ? (named ? 'nameAndValue' : 'value') : 'none';
    }
    if ('donut' in legacy) {
        if (isNil(legacy.innerSize)) ret.innerSize = legacy.donut ? defaultSpec.innerSize || 60 : 0;
        delete (ret as any).donut;
    }
    return ret;
}

//------------------------
// Spec helpers
//------------------------
export function findSeries(spec: ChartSpec, field: string): ChartSpecSeries {
    return spec.series.find(s => s.field === field) ?? null;
}

/** Visible series - for a pie in the 'series' layout, only the first is plotted. */
export function visibleSeries(spec: ChartSpec): ChartSpecSeries[] {
    return spec.series.filter(s => !s.hidden);
}

export function usesSecondaryAxis(spec: ChartSpec): boolean {
    return spec.layout === 'series' && spec.type !== 'pie' && visibleSeries(spec).some(s => s.axis === 'secondary');
}

/** Short, human-readable description of ops - used for undo labels and the assistant log. */
export function describeOps(ops: ChartSpecOp[]): string {
    return ops.map(describeOp).join(', ');
}

function describeOp(o: ChartSpecOp): string {
    switch (o.op) {
        case 'setOptions':
            return Object.entries(o.options)
                .map(([k, v]) => `${k}: ${isPlainObject(v) ? Object.values(v).join('/') : v}`)
                .join(', ');
        case 'addSeries':
            return `add ${o.series.field}`;
        case 'updateSeries':
            return `update ${o.field}`;
        case 'removeSeries':
            return `remove ${o.field}`;
        case 'moveSeries':
            return `move ${o.field}`;
        case 'updateAxis':
            return `${o.axis} axis`;
        case 'replaceSpec':
            return 'replace chart';
    }
}

//------------------------
// Reducer
//------------------------
function applyOp(spec: ChartSpec, o: ChartSpecOp, ctx: ChartSpecContext) {
    switch (o.op) {
        case 'setOptions':
            return setOptions(spec, o.options, ctx);
        case 'addSeries':
            return addSeries(spec, o.series, o.index, ctx);
        case 'updateSeries':
            return updateSeries(spec, o.field, o.changes, ctx);
        case 'removeSeries':
            return removeSeries(spec, o.field);
        case 'moveSeries':
            return moveSeries(spec, o.field, o.toIndex);
        case 'updateAxis':
            return updateAxis(spec, o.axis, o.changes);
        case 'replaceSpec':
            return replaceSpec(spec, o.spec, ctx);
        default:
            throw new OpError(`Unknown op '${(o as any).op}'`);
    }
}

function setOptions(spec: ChartSpec, options: ChartSpecOptions, ctx: ChartSpecContext) {
    check(isPlainObject(options), 'options must be an object');
    Object.entries(options).forEach(([key, v]) => {
        switch (key) {
            case 'type':
                check(CHART_TYPES.includes(v as any), `Unknown chart type '${v}'`);
                break;
            case 'layout':
                check(ctx.capabilities.layouts.includes(v as any), `This chart does not support the '${v}' layout`);
                break;
            case 'xField':
                if (v !== null) checkXField(v as string, ctx);
                break;
            case 'ohlc':
                if (v !== null) checkOhlc(v as ChartSpecOhlc, ctx);
                break;
            case 'candleStyle':
                check(CANDLE_STYLES.includes(v as any), `candleStyle must be one of ${CANDLE_STYLES.join(', ')}`);
                break;
            case 'legend':
                check(LEGEND_POSITIONS.includes(v as any), `legend must be one of ${LEGEND_POSITIONS.join(', ')}`);
                break;
            case 'stacking':
                check(STACKINGS.includes(v as any), `stacking must be one of ${STACKINGS.join(', ')}`);
                break;
            case 'dataLabels':
                check(DATA_LABELS.includes(v as any), `dataLabels must be one of ${DATA_LABELS.join(', ')}`);
                break;
            case 'innerSize':
                checkInnerSize(v);
                break;
            case 'sharedTooltip':
            case 'markers':
                check(isBoolean(v), `${key} must be boolean`);
                break;
            default:
                throw new OpError(`Unknown option '${key}'`);
        }
        (spec as any)[key] = isPlainObject(v) ? cloneDeep(v) : v;
    });

    if (spec.layout === 'categories') {
        // One point per field of the first row - there is no x field, and per-series settings don't apply
        spec.xField = null;
        spec.series.forEach(s => SERIES_LAYOUT_ONLY.forEach(k => delete s[k]));
    } else {
        check(spec.xField, "The 'series' layout needs an xField");
    }

    // Pie and bar charts can't mix in other series types - changing to one drops the overrides
    if (spec.type === 'pie' || spec.type === 'bar') spec.series.forEach(s => delete s.type);

    if (spec.type === 'candle') {
        check(spec.layout === 'series', "A candle chart needs the 'series' layout");
        // Changing to a candle chart without naming its fields takes a best guess at them
        spec.ohlc ??= defaultOhlc(ctx);
        check(spec.ohlc, 'This chart has no numeric fields to draw candles from');
    }
}

/** Best guess at the fields of a candle - by name, falling back to the close for any it can't find. */
function defaultOhlc(ctx: ChartSpecContext): ChartSpecOhlc {
    const fields = ctx.catalog.filter(f => f.type === 'number').map(f => f.name),
        find = (re: RegExp) => fields.find(f => re.test(f));
    if (isEmpty(fields)) return null;

    const close = find(/close$/i) ?? find(/avg$/i) ?? fields[0];
    return {open: find(/open$/i) ?? close, high: find(/high$/i) ?? close, low: find(/low$/i) ?? close, close};
}

function addSeries(spec: ChartSpec, series: ChartSpecSeries, index: number, ctx: ChartSpecContext) {
    check(isPlainObject(series), 'series must be an object');

    // Adding a series that already exists means "show it, with these settings"
    if (findSeries(spec, series.field)) {
        const {field, ...changes} = series;
        return updateSeries(spec, field, {hidden: null, ...changes}, ctx);
    }

    validateSeries(spec, series, ctx);
    const {length} = spec.series;
    spec.series.splice(isNumber(index) && index >= 0 && index < length ? index : length, 0, cloneDeep(series));
}

function updateSeries(spec: ChartSpec, field: string, changes: ChartSpecSeriesChanges, ctx: ChartSpecContext) {
    const series = requireSeries(spec, field);
    check(isPlainObject(changes), 'changes must be an object');
    check(!('field' in changes), 'A series field cannot be changed - remove and add a series instead');

    Object.entries(changes).forEach(([key, v]) => {
        if (v === null || v === undefined || (key === 'hidden' && v === false) || (key === 'axis' && v === 'primary')) delete series[key];
        else series[key] = v;
    });
    validateSeries(spec, series, ctx);
}

function removeSeries(spec: ChartSpec, field: string) {
    const series = requireSeries(spec, field);
    spec.series.splice(spec.series.indexOf(series), 1);
}

function moveSeries(spec: ChartSpec, field: string, toIndex: number) {
    check(isNumber(toIndex) && toIndex >= 0, 'toIndex must be a non-negative number');
    const series = requireSeries(spec, field);
    spec.series.splice(spec.series.indexOf(series), 1);
    spec.series.splice(Math.min(toIndex, spec.series.length), 0, series);
}

function updateAxis(spec: ChartSpec, axisId: AxisId, changes: ChartSpecAxisChanges) {
    check(AXIS_IDS.includes(axisId), `axis must be one of ${AXIS_IDS.join(', ')}`);
    check(isPlainObject(changes), 'changes must be an object');
    const axis = spec.axes[axisId];

    Object.entries(changes).forEach(([key, v]) => {
        if (v === null || v === undefined) {
            delete axis[key];
            return;
        }
        switch (key) {
            case 'title':
                check(isString(v), 'title must be a string');
                axis.title = v;
                break;
            case 'format':
                // Merge, so e.g. {format: {precision: 2}} keeps the existing number style
                axis.format = mergeFormat(axis.format, v as ChartSpecAxis['format']);
                validateFormat(axis.format, `${axisId} axis`);
                break;
            case 'min':
            case 'max':
                check(isNumber(v), `${key} must be a number`);
                axis[key] = v;
                break;
            default:
                throw new OpError(`Unknown axis setting '${key}'`);
        }
    });
    check(isNil(axis.min) || isNil(axis.max) || axis.min < axis.max, `${axisId} axis: min must be less than max`);
}

function replaceSpec(spec: ChartSpec, next: ChartSpec, ctx: ChartSpecContext) {
    check(isPlainObject(next) && isArray(next.series), 'spec must be a ChartSpec');

    const clean = normalizeSpec(next, spec);
    check(CHART_TYPES.includes(clean.type), `Unknown chart type '${clean.type}'`);
    check(ctx.capabilities.layouts.includes(clean.layout), `This chart does not support the '${clean.layout}' layout`);
    if (clean.layout === 'series') checkXField(clean.xField, ctx);
    check(CANDLE_STYLES.includes(clean.candleStyle), 'Unknown candleStyle');
    if (!isNil(clean.ohlc)) checkOhlc(clean.ohlc, ctx);
    if (clean.type === 'candle') check(clean.layout === 'series' && clean.ohlc, "A candle chart needs the 'series' layout and its ohlc fields");
    check(LEGEND_POSITIONS.includes(clean.legend), 'Unknown legend position');
    check(STACKINGS.includes(clean.stacking), 'Unknown stacking');
    check(DATA_LABELS.includes(clean.dataLabels), 'Unknown dataLabels');
    checkInnerSize(clean.innerSize);
    clean.series.forEach(s => validateSeries(clean, s, ctx));
    AXIS_IDS.forEach(id => {
        const {format, min, max} = clean.axes[id];
        if (!isNil(format)) validateFormat(format, `${id} axis`);
        check(isNil(min) || isNil(max) || min < max, `${id} axis: min must be less than max`);
    });

    Object.keys(spec).forEach(k => delete spec[k]);
    Object.assign(spec, clean);
}

//------------------------
// Validation
//------------------------
function checkInnerSize(v: any) {
    check(isNumber(v) && v >= 0 && v <= MAX_INNER_SIZE, `innerSize must be a percentage from 0 (a full pie) to ${MAX_INNER_SIZE}`);
}

function checkXField(field: string, ctx: ChartSpecContext) {
    const entry = checkField(field, ctx.catalog);
    check(entry.type !== 'bool', `'${field}' cannot be used as the x axis`);
}

function checkOhlc(ohlc: ChartSpecOhlc, ctx: ChartSpecContext) {
    check(isPlainObject(ohlc) && Object.keys(ohlc).every(k => OHLC_KEYS.includes(k as any)), `ohlc must be an object of ${OHLC_KEYS.join(', ')}`);
    OHLC_KEYS.forEach(k => {
        check(isString(ohlc[k]), `ohlc needs a field for ${k}`);
        check(checkField(ohlc[k], ctx.catalog).type === 'number', `'${ohlc[k]}' is not a number and cannot be used for ${k}`);
    });
}

function validateSeries(spec: ChartSpec, s: ChartSpecSeries, ctx: ChartSpecContext) {
    check(isPlainObject(s), 'Malformed series');
    const entry = checkField(s.field, ctx.catalog),
        {field} = s;
    check(entry.type === 'number', `'${field}' is not a number and cannot be plotted`);
    check(!spec.series.some(o => o !== s && o.field === field), `'${field}' is already plotted`);
    if (!isNil(s.name)) check(isString(s.name), `${field}: name must be a string`);
    if (!isNil(s.color)) check(SERIES_COLORS.includes(s.color), `${field}: unknown color '${s.color}' - use one of ${SERIES_COLORS.join(', ')}`);
    if (!isNil(s.hidden)) check(isBoolean(s.hidden), `${field}: hidden must be boolean`);

    if (spec.layout === 'categories') {
        SERIES_LAYOUT_ONLY.forEach(k => check(isNil(s[k]), `${field}: ${k} does not apply to the 'categories' layout`));
        return;
    }
    if (!isNil(s.type)) check(SERIES_TYPES.includes(s.type), `${field}: unknown series type '${s.type}'`);
    if (!isNil(s.axis)) check(s.axis === 'primary' || s.axis === 'secondary', `${field}: axis must be 'primary' or 'secondary'`);
    if (!isNil(s.dashed)) check(isBoolean(s.dashed), `${field}: dashed must be boolean`);
    validateSeriesTypes(spec);
}

/** Pie and bar charts can't mix in other series types. */
function validateSeriesTypes(spec: ChartSpec) {
    if (spec.type !== 'pie' && spec.type !== 'bar') return;
    const mixed = spec.series.find(s => !isEmpty(s.type));
    check(!mixed, `A ${spec.type} chart can't have a series of another type ('${mixed?.field}' is ${mixed?.type})`);
}

function requireSeries(spec: ChartSpec, field: string): ChartSpecSeries {
    const series = findSeries(spec, field);
    check(series, `No series '${field}' in the chart`);
    return series;
}
